/* eslint-disable import/prefer-default-export */
/*
 *  Copyright (c) 2014 The WebRTC project authors. All Rights Reserved.
 *
 *  Use of this source code is governed by a BSD-style license
 *  that can be found in the LICENSE file in the root of the source
 *  tree.
 */
//'use strict';
import VideoFrameChecker from './videoframechecker';
import Call from './call';

const util = require('./util.js');


function CamResolutionsTest(test, resolutions, deviceId) {
  this.test = test;
  this.resolutions = resolutions;
  this.currentResolution = 0;
  this.isMuted = false;
  this.isShuttingDown = false;
  this.deviceId = deviceId;
}

CamResolutionsTest.prototype = {
  run() {
    this.startGetUserMedia(this.resolutions[this.currentResolution], this.deviceId);
    this.currentResolution += 1;
  },

  startGetUserMedia(resolution, devId) {
    const constraints = {
      audio: false,
      video: {
        width: { exact: resolution[0] },
        height: { exact: resolution[1] },
        deviceId: devId,
      },
    };
    //util.appendSourceId(devId, 'video', constraints);
    navigator.mediaDevices.getUserMedia(constraints)
        .then((stream) => {
          // Do not check actual video frames when more than one resolution is
          // provided.
          if (this.resolutions.length > 1) {
            this.test.reportSuccess(`Supported resolution: ${resolution[0]}x${
            resolution[1]}`);
            stream.getTracks().forEach((track) => {
              track.stop();
            });
            this.maybeContinueGetUserMedia();
          } else {
            this.collectAndAnalyzeStats(stream, resolution);
          }
        })
        .catch((error) => {
          // if (error.name === "OverconstrainedError")
          // this.test.reportInfo('Resolution selected is not supported by your webcam');
          // else
          if (this.resolutions.length > 1) {
            this.test.reportInfo(`${resolution[0]}x${resolution[1]
            } not supported`);
          } else {
            this.test.reportError(`getUserMedia failed with error: ${
                error.name}`);
          }
          this.maybeContinueGetUserMedia();
        });
  },

  maybeContinueGetUserMedia() {
    if (this.currentResolution === this.resolutions.length) {
      this.test.done();
      return;
    }
    this.startGetUserMedia(this.resolutions[this.currentResolution]);
    this.currentResolution += 1;
  },

  collectAndAnalyzeStats(stream, resolution) {
    const tracks = stream.getVideoTracks();
    if (tracks.length < 1) {
      this.test.reportError('No video track in returned stream.');
      this.maybeContinueGetUserMedia();
      return;
    }

    // Firefox does not support event handlers on mediaStreamTrack yet.
    // https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack
    // TODO: remove if (...) when event handlers are supported by Firefox.
    const videoTrack = tracks[0];
    if (typeof videoTrack.addEventListener === 'function') {
      // Register events.
      videoTrack.addEventListener('ended', () => {
        // Ignore events when shutting down the test.
        if (this.isShuttingDown) {
          return;
        }
        this.test.reportError('Video track ended, camera stopped working');
      });
      videoTrack.addEventListener('mute', () => {
        // Ignore events when shutting down the test.
        if (this.isShuttingDown) {
          return;
        }
        this.test.reportWarning('Your camera reported itself as muted.');
        // MediaStreamTrack.muted property is not wired up in Chrome yet,
        // checking isMuted local state.
        this.isMuted = true;
      });
      videoTrack.addEventListener('unmute', () => {
        // Ignore events when shutting down the test.
        if (this.isShuttingDown) {
          return;
        }
        this.test.reportInfo('Your camera reported itself as unmuted.');
        this.isMuted = false;
      });
    }

    const video = document.createElement('video');
    video.setAttribute('autoplay', '');
    video.setAttribute('muted', '');
    video.width = resolution[0];
    video.height = resolution[1];
    video.srcObject = stream;
    const frameChecker = new VideoFrameChecker(video);
    const call = new Call(null, this.test);
    call.pc1.addStream(stream);
    call.establishConnection();
    call.gatherStats(
      call.pc1, null, stream,
      this.onCallEnded.bind(
        this, resolution, video,
        stream, frameChecker,
      ),
      100,
    );

    util.setTimeoutWithProgressBar(this.endCall.bind(this, call, stream), 8000);
  },

  onCallEnded(
    resolution, videoElement, stream, frameChecker,
    stats, statsTime,
  ) {
    this.analyzeStats(
      resolution, videoElement, stream, frameChecker,
      stats, statsTime,
    );

    frameChecker.stop();
    this.test.done();
  },

  analyzeStats(
    resolution, videoElement, stream,
    frameChecker, stats, statsTime,
  ) {
    const googAvgEncodeTime = [];
    const googAvgFrameRateInput = [];
    const googAvgFrameRateSent = [];
    const statsReport = {};
    const frameStats = frameChecker.frameStats;

    for (const index in stats) {
      if (stats[index].type === 'ssrc') {
        // Make sure to only capture stats after the encoder is setup.
        if (parseInt(stats[index].googFrameRateInput) > 0) {
          googAvgEncodeTime.push(parseInt(stats[index].googAvgEncodeMs));
          googAvgFrameRateInput.push(parseInt(stats[index].googFrameRateInput));
          googAvgFrameRateSent.push(parseInt(stats[index].googFrameRateSent));
        }
      }
    }

    statsReport.cameraName = stream.getVideoTracks()[0].label || NaN;
    statsReport.actualVideoWidth = videoElement.videoWidth;
    statsReport.actualVideoHeight = videoElement.videoHeight;
    statsReport.mandatoryWidth = resolution[0];
    statsReport.mandatoryHeight = resolution[1];
    statsReport.encodeSetupTimeMs = this.extractEncoderSetupTime(stats, statsTime);
    statsReport.avgEncodeTimeMs = util.arrayAverage(googAvgEncodeTime);
    statsReport.minEncodeTimeMs = util.arrayMin(googAvgEncodeTime);
    statsReport.maxEncodeTimeMs = util.arrayMax(googAvgEncodeTime);
    statsReport.avgInputFps = util.arrayAverage(googAvgFrameRateInput);
    statsReport.minInputFps = util.arrayMin(googAvgFrameRateInput);
    statsReport.maxInputFps = util.arrayMax(googAvgFrameRateInput);
    statsReport.avgSentFps = util.arrayAverage(googAvgFrameRateSent);
    statsReport.minSentFps = util.arrayMin(googAvgFrameRateSent);
    statsReport.maxSentFps = util.arrayMax(googAvgFrameRateSent);
    statsReport.isMuted = this.isMuted;
    statsReport.testedFrames = frameStats.numFrames;
    statsReport.blackFrames = frameStats.numBlackFrames;
    statsReport.frozenFrames = frameStats.numFrozenFrames;

    // TODO: Add a reportInfo() function with a table format to display
    // values clearer.
    //report.traceEventInstant('video-stats', statsReport);

    this.testExpectations(statsReport);
  },

  endCall(callObject, stream) {
    this.isShuttingDown = true;
    stream.getTracks().forEach((track) => {
      track.stop();
    });
    callObject.close();
  },

  extractEncoderSetupTime(stats, statsTime) {
    for (let index = 0; index !== stats.length; index++) {
      if (stats[index].type === 'ssrc') {
        if (parseInt(stats[index].googFrameRateInput) > 0) {
          return JSON.stringify(statsTime[index] - statsTime[0]);
        }
      }
    }
    return NaN;
  },

  resolutionMatchesIndependentOfRotationOrCrop(
aWidth, aHeight,
    bWidth, bHeight,
) {
    const minRes = Math.min(bWidth, bHeight);
    return (aWidth === bWidth && aHeight === bHeight) ||
            (aWidth === bHeight && aHeight === bWidth) ||
            (aWidth === minRes && bHeight === minRes);
  },

  testExpectations(info) {
    const notAvailableStats = [];
    for (const key in info) {
      if (info.hasOwnProperty(key)) {
        if (typeof info[key] === 'number' && isNaN(info[key])) {
          notAvailableStats.push(key);
        } else {
          this.test.reportInfo(`${key}: ${info[key]}`);
        }
      }
    }
    if (notAvailableStats.length !== 0) {
      this.test.reportInfo(`Not available: ${notAvailableStats.join(', ')}`);
    }

    if (isNaN(info.avgSentFps)) {
      this.test.reportInfo('Cannot verify sent FPS.');
    } else if (info.avgSentFps < 5) {
      this.test.reportError(`Low average sent FPS: ${info.avgSentFps}`);
    } else {
      this.test.reportSuccess('Average FPS above threshold');
    }
    if (!this.resolutionMatchesIndependentOfRotationOrCrop(
        info.actualVideoWidth, info.actualVideoHeight, info.mandatoryWidth,
        info.mandatoryHeight, 
  )) {
          this.test.reportError('Incorrect captured resolution.');
    } else {
      this.test.reportSuccess('Captured video using expected resolution.');
    }
    if (info.testedFrames === 0) {
      this.test.reportError('Could not analyze any video frame.');
    } else {
      if (info.blackFrames > info.testedFrames / 3) {
        this.test.reportError('Camera delivering lots of black frames.');
      }
      if (info.frozenFrames > info.testedFrames / 3) {
        this.test.reportError('Camera delivering lots of frozen frames.');
      }
    }
  },
};

export function testCamera(test, deviceId) {
  switch (test.test_title) {
    case 'check_resolution_240': {
      const camera = new CamResolutionsTest(test, [[320, 240]], deviceId);
      camera.run();

      break;
    }
    case 'check_resolution_480': {
      const camera = new CamResolutionsTest(test, [[640, 480]], deviceId);
      camera.run();
      break;
    }
    case 'check_resolution_720': {
      const camera = new CamResolutionsTest(test, [[1280, 720]], deviceId);
      camera.run();
      break;
    }
    case 'check_supported_resolutions': {
      const resolutionArray = [
        [160, 120], [320, 180], [320, 240], [640, 360], [640, 480], [768, 576],
        [1024, 576], [1280, 720], [1280, 768], [1280, 800], [1920, 1080],
        [1920, 1200], [3840, 2160], [4096, 2160],
      ];
      const camResolutionsTest = new CamResolutionsTest(test, resolutionArray, deviceId);
      camResolutionsTest.run();
     break;
    }
    default:
      break;
  }
}



//////////////////
// WEBPACK FOOTER
// ./src/testWebRtc/camresolutionstest.js
// module id = 207
// module chunks = 0